import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

export default function ResultPage() {
  const { attemptId } = useParams();
  const location      = useLocation();
  const navigate      = useNavigate();
  const { user }      = useAuth();
  const [result,      setResult]      = useState(location.state?.result || null);
  const [attempt,     setAttempt]     = useState(null);
  const [examConfig,  setExamConfig]  = useState(null); // has student_can_download_result
  const [loading,     setLoading]     = useState(true);
  const [blocked,     setBlocked]     = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // 1. Get attempt details
        const myRes = await axios.get('/api/attempts/my');
        const a = myRes.data.find(x => x.id === attemptId);
        if (a) {
          setAttempt(a);
          if (!result && a) setResult({ score: a.score, percentage: a.percentage, passed: a.passed, total_marks: a.total_marks });

          // 2. FIX 7: Check exam's result permission setting
          try {
            const examRes = await axios.get(`/api/exams/${a.exam_id}/settings-public`);
            const cfg = examRes.data;
            setExamConfig(cfg);
            // If student_can_download_result is false, block the page entirely
            if (cfg.student_can_download_result === false) {
              setBlocked(true);
            }
          } catch {
            // If we can't load the setting, allow by default (graceful degradation)
          }
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    load();
  }, [attemptId]); // eslint-disable-line

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  // FIX 7: Result blocked by org setting
  if (blocked) {
    return (
      <div className="layout">
        <Sidebar />
        <main className="main-content">
          <div style={{ maxWidth: 500, margin:'80px auto', textAlign:'center' }}>
            <div style={{ fontSize:56, marginBottom:16 }}>🔒</div>
            <h2 style={{ fontSize:22, fontWeight:700, marginBottom:10 }}>Result Unavailable</h2>
            <p style={{ fontSize:15, color:'var(--text-secondary)', lineHeight:1.7 }}>
              Result is currently unavailable. Please contact your organization.
            </p>
            <button className="btn btn-secondary" style={{ marginTop:24 }}
              onClick={() => navigate('/dashboard')}>← Back to Dashboard</button>
          </div>
        </main>
      </div>
    );
  }

  const pct    = result ? parseFloat(result.percentage) : 0;
  const passed = result?.passed;
  const circleColor = passed ? 'var(--accent-success)' : 'var(--accent-danger)';
  const grade  = pct >= 90 ? 'A+' : pct >= 80 ? 'A' : pct >= 70 ? 'B' : pct >= 60 ? 'C' : pct >= 50 ? 'D' : 'F';
  const getBarWidth = (val, max) => max > 0 ? `${Math.min(100, (val / max) * 100)}%` : '0%';

  const canDownload = examConfig?.student_can_download_result !== false;

  const handleDownloadPDF = () => {
    if (!canDownload) { toast.error('Result download is not permitted for this exam.'); return; }
    const candidateName = user?.full_name || attempt?.student_name || 'Student';
    const orgName       = attempt?.org_name || '';
    const now           = new Date().toLocaleString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true });
    const pw = window.open('', '_blank');
    pw.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Result — ${candidateName}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;padding:32px;color:#111}
      .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:16px;border-bottom:2px solid #3b82f6}
      .org{font-size:18px;font-weight:800;color:#3b82f6}.tag{font-size:10px;color:#888;margin-top:2px}
      h1{font-size:20px;margin-bottom:4px}.sub{color:#666;font-size:12px;margin-bottom:20px}
      .result-box{border:2px solid ${passed?'#16a34a':'#dc2626'};border-radius:10px;padding:20px;margin-bottom:16px;text-align:center}
      .pct{font-size:56px;font-weight:900;color:${passed?'#16a34a':'#dc2626'};line-height:1}
      .verdict{font-size:18px;font-weight:700;margin-top:8px;color:${passed?'#16a34a':'#dc2626'}}
      .grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-top:16px}
      .stat{text-align:center;border:1px solid #e5e7eb;border-radius:8px;padding:12px}
      .sv{font-size:24px;font-weight:800;color:#1d4ed8}.sl{font-size:11px;color:#888;margin-top:3px}
      .info-table{width:100%;border-collapse:collapse;margin-top:16px}
      .info-table td{padding:8px 12px;border-bottom:1px solid #f0f0f0;font-size:13px}
      .info-table td:first-child{font-weight:600;color:#555;width:40%}
      .footer{margin-top:20px;font-size:10px;color:#aaa;text-align:right;padding-top:12px;border-top:1px solid #eee}
      @media print{@page{margin:12mm}body{padding:0}}</style>
    </head><body>
      <div class="header">
        <div><div class="org">${orgName || 'ExamAI'}</div><div class="tag">Online Examination System</div></div>
        <div style="text-align:right"><div class="tag">Generated</div><div style="font-size:11px">${now} IST</div></div>
      </div>
      <h1>Exam Result Certificate</h1>
      <div class="sub">${attempt?.exam_title || '—'}</div>

      <div class="result-box">
        <div class="pct">${Math.round(pct)}%</div>
        <div class="verdict">${passed ? '🎉 PASSED' : '😔 NOT PASSED'}</div>
        <div class="grid">
          <div class="stat"><div class="sv" style="color:${passed?'#16a34a':'#dc2626'}">${grade}</div><div class="sl">Grade</div></div>
          <div class="stat"><div class="sv">${result?.score??'—'}/${result?.total_marks}</div><div class="sl">Marks</div></div>
          <div class="stat"><div class="sv">${Math.floor((attempt?.time_spent_seconds||0)/60)}m</div><div class="sl">Time Spent</div></div>
        </div>
      </div>

      <table class="info-table">
        <tr><td>Candidate Name</td><td><strong>${candidateName}</strong></td></tr>
        <tr><td>Exam Name</td><td>${attempt?.exam_title || '—'}</td></tr>
        ${orgName ? `<tr><td>Organization</td><td>${orgName}</td></tr>` : ''}
        <tr><td>Attempt ID</td><td style="font-family:monospace;font-size:11px">${attemptId}</td></tr>
        <tr><td>Date & Time</td><td>${now} IST</td></tr>
        <tr><td>Passing Score</td><td>${attempt?.passing_marks ?? '—'} / ${result?.total_marks}</td></tr>
        <tr><td>Result</td><td><strong style="color:${passed?'#16a34a':'#dc2626'}">${passed?'PASS':'FAIL'}</strong></td></tr>
      </table>

      <div class="footer">ExamAI Proctored Examination Platform · Result generated on ${now} IST</div>
    </body></html>`);
    pw.document.close();
    pw.onload = () => { pw.focus(); pw.print(); };
  };

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content">
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <div className="page-header" style={{ textAlign: 'center', position:'relative' }}>
            <h1 className="page-title">Exam Results</h1>
            <p className="page-subtitle" style={{ fontSize:14 }}>
              {user?.full_name && <strong>{user.full_name}</strong>}
              {attempt?.exam_title && <> &mdash; {attempt.exam_title}</>}
            </p>
            {canDownload && (
              <button className="btn btn-secondary btn-sm"
                style={{ position:'absolute', right:0, top:0 }}
                onClick={handleDownloadPDF}>
                📄 Download PDF
              </button>
            )}
          </div>

          {/* Result circle */}
          <div className="card" style={{ textAlign: 'center', padding: 48, marginBottom: 24 }}>
            <div className="result-circle" style={{ borderColor: circleColor }}>
              <div className="result-score" style={{ color: circleColor }}>{Math.round(pct)}%</div>
              <div className="result-label">Score</div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
              {passed ? '🎉 Congratulations!' : '😔 Better luck next time'}
            </div>
            <div style={{ fontSize: 16, color: 'var(--text-secondary)', marginBottom: 24 }}>
              {passed ? 'You have passed the exam' : 'You did not meet the passing criteria'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-primary)' }}>
                  {result?.score}/{result?.total_marks}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Marks Obtained</div>
              </div>
              <div>
                <div style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-secondary)' }}>{grade}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Grade</div>
              </div>
              {attempt && (
                <div>
                  <div style={{ fontSize: 32, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--accent-warning)' }}>
                    {Math.floor((attempt.time_spent_seconds || 0) / 60)}m
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Time Spent</div>
                </div>
              )}
            </div>
          </div>

          {/* Score breakdown */}
          <div className="card" style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 20 }}>Score Breakdown</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Your Score', val: result?.score, max: result?.total_marks, color: circleColor },
                { label: 'Passing Score', val: attempt?.passing_marks, max: result?.total_marks, color: 'var(--accent-warning)' },
              ].map(item => item.val !== undefined && (
                <div key={item.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', color: item.color }}>{item.val}/{item.max}</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: item.color, borderRadius: 4, width: getBarWidth(item.val, item.max), transition: 'width 1s ease' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance rating */}
          <div className="card" style={{ marginBottom: 32 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Performance Rating</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {['F (<50%)', 'D (50%)', 'C (60%)', 'B (70%)', 'A (80%+)'].map((label, i) => {
                const active = i === (pct >= 80 ? 4 : pct >= 70 ? 3 : pct >= 60 ? 2 : pct >= 50 ? 1 : 0);
                return (
                  <div key={label} style={{
                    padding: '10px 8px', borderRadius: 8, textAlign: 'center',
                    background: active ? 'rgba(79,142,247,0.15)' : 'var(--bg-elevated)',
                    border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border)'}`,
                    fontSize: 11, color: active ? 'var(--accent-primary)' : 'var(--text-muted)'
                  }}>
                    {label}
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>← Dashboard</button>
          </div>
        </div>
      </main>
    </div>
  );
}
